import React, { useEffect, useRef } from "react";
import Select from "react-select";
import { Controller } from "react-hook-form";
import { motion } from "framer-motion";
import { FaTrash } from "react-icons/fa";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "32px",
    height: "32px",
    borderColor: state.isFocused ? "#A78BFA" : "#D1D5DB",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(167,139,250,0.35)" : "none",
    fontSize: "12px",
    backgroundColor: "white",
  }),
  indicatorsContainer: (b) => ({ ...b, height: "30px" }),
  dropdownIndicator: (b) => ({ ...b, padding: "4px" }),
  clearIndicator: (b) => ({ ...b, padding: "4px" }),
  valueContainer: (b) => ({ ...b, padding: "0 6px", height: "30px" }),
  input: (b) => ({ ...b, margin: 0, padding: 0 }),
  menu: (b) => ({ ...b, fontSize: "12px", zIndex: 50 }),
  menuList: (b) => ({ ...b, maxHeight: "100px", overflowY: "auto" }),
  singleValue: (b) => ({ ...b, fontSize: "12px" }),
};

const LooseDiamondItemForm = ({
  control,
  register,
  errors,
  rowindex,
  fieldId,
  firstFieldRef,
  registerFirstField,
  onDelete,
  cardHeightClass = "h-[40vh] sm:h-[40vh]",

  /* options */
  diamondOptions,
  shapeOptions,
  clarityOptions,
  colorOptions,
  sieveOptions,
  sizeOptions,

  setValue,
}) => {
  const formRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === "Delete") {
        if (formRef.current && formRef.current.contains(document.activeElement)) {
          e.preventDefault();
          onDelete?.();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onDelete]);

  const path = (name) => `looseDiamonds.${rowindex}.${name}`;

  /* shape-bound sieve/size filter */
  const filterByShape = (arr, selectedShape) => {
    if (!selectedShape?.value) return arr;
    return arr.filter((x) => String(x.shape) === String(selectedShape.value));
  };

  return (
    <motion.div
      ref={formRef}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: rowindex * 0.04 }}
      id={`loose-diamond-card-${fieldId}`}
      data-field-id={fieldId}
      className={`stock-card ${cardHeightClass} snap-start p-2 rounded-lg border border-gray-200 hover:border-gray-300 shadow-lg mb-0 bg-white relative overflow-y-auto`}
    >
      {/* Header */}
      <div className="mb-3 flex items-center justify-between sticky top-0 bg-white pt-1 pb-2">
        <h3 className="text-sm font-semibold text-gray-900">#{rowindex + 1} • Loose Diamond</h3>
        <div className="flex-1 h-px bg-gray-200 mx-2" />
        <button
          type="button"
          onClick={onDelete}
          className="text-gray-500 hover:text-red-600 transition-all duration-200 hover:scale-110"
          title="Delete this row (Ctrl + Delete)"
        >
          <FaTrash className="text-[14px]" />
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 md:grid-cols-12 gap-2">
        {/* Diamond Name */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Diamond Name <span className="text-red-500">*</span>
          </label>
          <Controller
            name={path("diamondName")}
            control={control}
            rules={{ required: "Diamond name is required" }}
            render={({ field: { onChange, ...field } }) => (
              <Select
                {...field}
                ref={(ref) => {
                  field.ref(ref);
                  if (registerFirstField) registerFirstField(fieldId, { focus: () => ref?.focus?.() });
                  if (firstFieldRef) firstFieldRef.current = ref;
                }}
                options={diamondOptions}
                classNamePrefix="react-select"
                isClearable
                isSearchable
                styles={selectStyles}
                onChange={(opt) => {
                  onChange(opt ?? null);
                  // derive shape/color/clarity from selected diamond (if present)
                  if (!opt?.info) {
                    setValue(path("diamondShape"), null);
                    setValue(path("diamondClarity"), null);
                    setValue(path("diamondColor"), null);
                    return;
                  }
                  const d = opt.info;
                  setValue(
                    path("diamondShape"),
                    d?.shape?._id ? { value: d.shape._id, label: d.shapeName || d.shape.name } : null
                  );
                  setValue(
                    path("diamondClarity"),
                    d?.clarity?._id ? { value: d.clarity._id, label: d.clarityName || d.clarity.name } : null
                  );
                  setValue(
                    path("diamondColor"),
                    d?.color?._id ? { value: d.color._id, label: d.colorName || d.color.name } : null
                  );
                }}
              />
            )}
          />
          {errors?.looseDiamonds?.[rowindex]?.diamondName && (
            <p className="text-red-500 text-xs mt-1">
              {errors.looseDiamonds[rowindex].diamondName.message}
            </p>
          )}
        </div>

        {/* Shape */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Shape</label>
          <Controller
            name={path("diamondShape")}
            control={control}
            render={({ field }) => (
              <Select {...field} isClearable options={shapeOptions} styles={selectStyles} />
            )}
          />
        </div>

        {/* Clarity */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Clarity</label>
          <Controller
            name={path("diamondClarity")}
            control={control}
            render={({ field }) => (
              <Select {...field} isClearable options={clarityOptions} styles={selectStyles} />
            )}
          />
        </div>

        {/* Color */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Color</label>
          <Controller
            name={path("diamondColor")}
            control={control}
            render={({ field }) => (
              <Select {...field} isClearable options={colorOptions} styles={selectStyles} />
            )}
          />
        </div>

        {/* Sieve (filtered by shape if selected) */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Sieve</label>
          <Controller
            name={path("diamondSieve")}
            control={control}
            render={({ field, formState }) => {
              const selectedShape = formState?.defaultValues
                ? formState.defaultValues.looseDiamonds?.[rowindex]?.diamondShape
                : null;
              return (
                <Select
                  {...field}
                  isClearable
                  options={filterByShape(sieveOptions, selectedShape || field.value)}
                  styles={selectStyles}
                />
              );
            }}
          />
        </div>

        {/* Size (filtered by shape if selected) */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Size</label>
          <Controller
            name={path("diamondSize")}
            control={control}
            render={({ field, formState }) => {
              const selectedShape = formState?.defaultValues
                ? formState.defaultValues.looseDiamonds?.[rowindex]?.diamondShape
                : null;
              return (
                <Select
                  {...field}
                  isClearable
                  options={filterByShape(sizeOptions, selectedShape || field.value)}
                  styles={selectStyles}
                />
              );
            }}
          />
        </div>

        {/* Weight (ct) */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Weight (ct) <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register(path("diamondWt"), { required: "Weight is required" })}
            placeholder="e.g., 0.85"
            className="w-full px-3 py-[6px] text-sm border border-gray-300 rounded-md bg-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300"
          />
          {errors?.looseDiamonds?.[rowindex]?.diamondWt && (
            <p className="text-red-500 text-xs mt-1">
              {errors.looseDiamonds[rowindex].diamondWt.message}
            </p>
          )}
        </div>

        {/* Pieces */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Pieces</label>
          <input
            type="text"
            {...register(path("diamondPcs"))}
            placeholder="e.g., 12"
            className="w-full px-3 py-[6px] text-sm border border-gray-300 rounded-md bg-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300"
          />
        </div>

        {/* Description */}
        <div className="md:col-span-4">
          <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
          <input
            type="text"
            {...register(path("diamondDescription"))}
            placeholder="Optional"
            className="w-full px-3 py-[6px] text-sm border border-gray-300 rounded-md bg-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300"
          />
        </div>
      </div>
    </motion.div>
  );
};

export default LooseDiamondItemForm;
